import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmDeleteDialog from '../../../security/ConfirmDeleteDialog';
import { ENDPOINTS } from '../../Utils/Endpoints';
import { Models } from '../../Utils/ModelsLMS';
import sigmaDelete from '../../../Pages/img/Borrar.png';
import { normalizeValue } from '../../../../utils/Texts';
import { getSessionUserId } from '../../../../utils/SessionUser';
import AlertDialog from '../Shared/AlertDialog';

const TITLETAB = 'Asignación de Cursos al Nivel';

export default function SemestreCursoTab({ fkValue, onSaveSuccess, initialName, idCarrera, isReadOnly }) {
    const [loading, setLoading] = useState(false);
    const [allCourses, setAllCourses] = useState([]);
    const [initialAssignments, setInitialAssignments] = useState([]);
    const [currentAssignments, setCurrentAssignments] = useState([]);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [instructorId, setInstructorId] = useState(null);
    const [certificatedCourseIds, setCertificatedCourseIds] = useState(new Set());
    const [assignedInOtherSemestersIds, setAssignedInOtherSemestersIds] = useState(new Set());

    // Modal de alertas estándar LMS
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });

    // Validar si venimos en modo edición de nombre
    const isEditingName = initialName !== undefined && idCarrera;
    const [localName, setLocalName] = useState(initialName || '');

    useEffect(() => {
        if (initialName) setLocalName(initialName);
    }, [initialName]);

    const load = async () => {
        setLoading(true);
        try {
            const sessionUserId = getSessionUserId();
            let currentIid = null;
            if (sessionUserId) {
                const resInst = await axios.get(ENDPOINTS.instructorByUsuario(sessionUserId));
                if (resInst.data) {
                    const instData = Array.isArray(resInst.data) ? resInst.data[0] : resInst.data;
                    currentIid = instData.idInstructor || instData.IdInstructor;
                    setInstructorId(currentIid);
                }
            }

            const [relRes, cursoRes, certRes, semRes] = await Promise.all([
                axios.get(ENDPOINTS.semestreCurso),
                axios.get(ENDPOINTS.cursosAdminHistorico),
                axios.get(ENDPOINTS.certificaciones),
                axios.get(ENDPOINTS.semestres)
            ]);

            const relationships = (relRes.data || []).map(Models.semestreCurso);
            const courses = (cursoRes.data || []).map(Models.cursos);
            const certifications = (certRes.data || []).map(Models.certificaciones);
            const semestres = (semRes.data || []).map(Models.semestre);

            // Filter for current semester
            const myAssignments = relationships.filter(r => Number(r.idSemestre) === Number(fkValue));

            // Set of course IDs that have a generated certificate
            const certIds = new Set(certifications.map(c => c.idCurso));

            // Logic to find courses assigned to OTHER semesters of the same career
            if (idCarrera) {
                const careerSems = semestres.filter(s => Number(s.idCarrera) === Number(idCarrera));
                const otherSemIds = new Set(
                    careerSems
                        .filter(s => Number(s.idSemestre) !== Number(fkValue))
                        .map(s => s.idSemestre)
                );

                const otherAssignments = relationships.filter(r => otherSemIds.has(Number(r.idSemestre)));
                const otherCourseIds = new Set(otherAssignments.map(r => r.idCurso));
                setAssignedInOtherSemestersIds(otherCourseIds);
            }

            setCertificatedCourseIds(certIds);
            setAllCourses(courses);
            setInitialAssignments(myAssignments);
            setCurrentAssignments(myAssignments);
        } catch (e) {
            console.error(e);
            setAlertDialog({
                open: true,
                title: 'Error de Carga',
                message: `Error al cargar ${TITLETAB}`,
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (fkValue) load();
    }, [fkValue]);

    // Available courses (Total - Assigned) + Filters (Terminado, Certificado, Instructor)
    const availableCourses = useMemo(() => {
        const assignedIds = new Set(currentAssignments.map(a => a.idCurso));

        let list = allCourses.filter(c => {
            // 1. Not already assigned to THIS semester
            if (assignedIds.has(c.idCurso)) return false;

            // 2. Not assigned to ANY OTHER semester of the same career
            if (assignedInOtherSemestersIds.has(c.idCurso)) return false;

            // 3. Must be finished (Terminado = true)
            if (!c.terminado) return false;

            // 4. Must be of current instructor
            if (instructorId && Number(c.idInstructor) !== Number(instructorId)) return false;

            // 5. Must have a generated certificate
            if (!certificatedCourseIds.has(c.idCurso)) return false;

            // 6. MUST BE ACTIVE (per user request: only see active on the left)
            if (!c.activo) return false;

            return true;
        });

        if (query.trim()) {
            const q = query.toLowerCase().trim();
            list = list.filter(c => normalizeValue(c.nombre).toLowerCase().includes(q));
        }
        return list;
    }, [allCourses, currentAssignments, query, instructorId, certificatedCourseIds, assignedInOtherSemestersIds]);

    // Enriched assigned list
    const enrichedAssignments = useMemo(() => {
        return currentAssignments.map(a => {
            const courseDef = allCourses.find(c => c.idCurso === a.idCurso);
            return {
                ...a,
                nombreCurso: courseDef ? normalizeValue(courseDef.nombre) : `Curso #${a.idCurso}`
            };
        });
    }, [currentAssignments, allCourses]);

    // Check for changes
    const hasChanges = useMemo(() => {
        if (isEditingName && localName !== initialName) return true;
        if (initialAssignments.length !== currentAssignments.length) return true;
        const initialIds = new Set(initialAssignments.map(a => a.idCurso));
        return currentAssignments.some(a => !initialIds.has(a.idCurso));
    }, [initialAssignments, currentAssignments, isEditingName, localName, initialName]);

    // --- Drag and Drop ---
    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetColumn) => {
        e.preventDefault();
        if (isReadOnly) return;
        if (dragOverColumn !== targetColumn) setDragOverColumn(targetColumn);
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === targetColumn) return;

        if (targetColumn === 'assigned') addLocal(item);
        else removeLocal(item);
    };

    // --- Logic ---
    const addLocal = (course) => {
        if (currentAssignments.some(a => a.idCurso === course.idCurso)) return;
        const fresh = {
            idSemestreCurso: 0,
            idSemestre: Number(fkValue),
            idCurso: course.idCurso,
            activo: true
        };
        setCurrentAssignments(prev => [...prev, fresh]);
    };

    const removeLocal = (assignment) => {
        setCurrentAssignments(prev => prev.filter(a => a.idCurso !== assignment.idCurso));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const promises = [];

            // 1. Update Semester Name if needed
            if (isEditingName && localName !== initialName) {
                const dto = {
                    idSemestre: Number(fkValue),
                    idCarrera: Number(idCarrera),
                    nombre: localName,
                    activo: true
                };
                promises.push(axios.put(ENDPOINTS.semestres, dto));
            }

            // 2. Additions / Deletions
            const initialIds = new Set(initialAssignments.map(a => a.idCurso));
            const toAdd = currentAssignments.filter(a => !initialIds.has(a.idCurso));

            const currentIds = new Set(currentAssignments.map(a => a.idCurso));
            const toRemove = initialAssignments.filter(a => !currentIds.has(a.idCurso));

            // Additions
            for (const item of toAdd) {
                promises.push(axios.post(ENDPOINTS.semestreCurso, item));
            }
            // Deletions
            for (const item of toRemove) {
                if (item.idSemestreCurso > 0) {
                    promises.push(axios.delete(ENDPOINTS.semestreCursoById(item.idSemestreCurso)));
                }
            }

            await Promise.all(promises);
            await load();
            if (onSaveSuccess) {
                onSaveSuccess();
            } else {
                setAlertDialog({
                    open: true,
                    title: 'Éxito',
                    message: 'Cambios guardados correctamente',
                    variant: 'success'
                });
            }
        } catch (e) {
            console.error(e);
            setAlertDialog({
                open: true,
                title: 'Error al Guardar',
                message: 'Error al guardar los cambios.',
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (isEditingName) setLocalName(initialName);
        setCurrentAssignments(initialAssignments);
    };

    return (
        <div className="mini-crud">
            <div className="mini-topbar">
                <div style={{ width: '100%' }}>
                    {/* Input de Nombre si está habilitado */}
                    {isEditingName ? (
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1e293b' }}>
                                Nombre del Nivel
                            </label>
                            <input
                                type="text"
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '1rem',
                                    outline: 'none'
                                }}
                                value={localName}
                                onChange={e => setLocalName(e.target.value)}
                                placeholder="Ej. Primer Nivel"
                                disabled={isReadOnly}
                            />
                        </div>
                    ) : (
                        <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
                    )}

                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                        {isReadOnly ? 'Vista de solo lectura - Carrera publicada' : 'Arrastra cursos entre las columnas o usa los botones (＋ / ×). No olvides Guardar.'}
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
                <input
                    className="roles-search"
                    placeholder="Filtrar cursos disponibles..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ width: '100%', maxWidth: '400px' }}
                />
            </div>

            <div className="dnd-container">
                {/* Disponibles */}
                <div
                    className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                    onDragOver={e => handleDragOver(e, 'available')}
                    onDrop={e => handleDrop(e, 'available')}
                >
                    <div className="dnd-header">Cursos Disponibles</div>
                    {availableCourses.length === 0 ? (
                        <div className="dnd-empty">{query ? 'Sin resultados' : 'No hay más cursos'}</div>
                    ) : (
                        availableCourses.map(c => (
                            <div
                                key={c.idCurso}
                                className="dnd-item"
                                draggable={!saving && !isReadOnly}
                                onDragStart={e => handleDragStart(e, c, 'available')}
                                style={{ cursor: isReadOnly ? 'default' : 'grab' }}
                            >
                                <span>{normalizeValue(c.nombre)}</span>
                                {!isReadOnly && (
                                    <button className="rk-iconbtn--edit"
                                        style={{ background: 'transparent', border: 'none', color: '#28a745', fontSize: '1.2rem', cursor: 'pointer' }}
                                        onClick={() => addLocal(c)}
                                    >＋</button>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Asignados */}
                <div
                    className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                    onDragOver={e => handleDragOver(e, 'assigned')}
                    onDrop={e => handleDrop(e, 'assigned')}
                >
                    <div className="dnd-header">Cursos en este Nivel</div>
                    {enrichedAssignments.length === 0 ? (
                        <div className="dnd-empty">Arrastra cursos aquí</div>
                    ) : (
                        enrichedAssignments.map(a => (
                            <div
                                key={a.idCurso}
                                className="dnd-item"
                                draggable={!saving && !isReadOnly}
                                onDragStart={e => handleDragStart(e, a, 'assigned')}
                                style={{ cursor: isReadOnly ? 'default' : 'grab' }}
                            >
                                <span>{a.nombreCurso}</span>
                                {!isReadOnly && (
                                    <button
                                        style={{ background: 'transparent', border: 'none', color: '#dc3545', fontSize: '1.2rem', cursor: 'pointer' }}
                                        onClick={() => removeLocal(a)}
                                    >×</button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1rem' }}>
                <button className="roles-btn" onClick={onSaveSuccess || handleCancel} disabled={saving}>
                    {isReadOnly ? 'Cerrar' : 'Cerrar'}
                </button>
                {!isReadOnly && (
                    <button
                        className="btn-action"
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        style={{ opacity: (!hasChanges || saving) ? 0.6 : 1 }}
                    >
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                )}
            </div>
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}
